
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import FileUploadZone from "../uploads/FileUploadZone";
import UploadProgressDisplay from "../common/UploadProgressDisplay";
import apiClient from "../utils/apiClient";
import { 
  Map, 
  Play, 
  Upload, 
  CheckCircle, 
  ArrowRight, 
  ExternalLink,
  FileText,
  Video,
  AlertCircle
} from "lucide-react";

const KMLSetup = () => {
  // Extract projectId from URL parameters
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [showUpload, setShowUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState(null);

  const handleUploadKML = async (files) => {
    if (files.length === 0) return;

    const file = files[0];

    try {
      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Uploading KML file...",
      }));

      // const destPath = `gui/${projectId}/kml/${file.name}`;
      const destPath = `projects/${projectId}/01_Input/GeospatialData/KML/${file.name}`;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("destPath", destPath);
      formData.append("overwrite", "true");

      await apiClient.uploadFile(
        "/api/files/upload-form",
        file,
        {
          destPath,
          overwrite: "true",
        },
        {
          useOperational: true,
        }
      );

      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Complete",
      }));

      setUploadStatus("success");

      setTimeout(() => {
        setUploadProgress({});
        setUploadStatus(null);
      }, 2000);
    } catch (error) {
      console.error("Error uploading KML file:", error);
      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Failed",
      }));
      setUploadStatus("error");
    }
  };

  const handleContinue = () => {
    // Navigate to the next step in your workflow
    navigate(`/project-processing/${projectId}`);
  };

  const namingProtocol = [
    {
      layer: "TargetBuilding",
      example: "target_building_01",
      selection: "Individual outline of building",
      color: "from-blue-500 to-indigo-500"
    },
    {
      layer: "SurroundingBuildings",
      example: "surrounding_buildings_01",
      selection: "Area incl. relevant buildings",
      color: "from-indigo-500 to-purple-500"
    },
    {
      layer: "Topography",
      example: "terrain_01",
      selection: "Area incl. relevant terrain",
      color: "from-purple-500 to-pink-500"
    },
    {
      layer: "Vegetation",
      example: "deciduous_tree_01",
      selection: "Individual trees",
      color: "from-pink-500 to-rose-500"
    }
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
              <Map className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-4">
              KML Setup
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Configure your geospatial data by creating and uploading a properly formatted KML file
            </p>
          </div>

          {/* Step 1: Tutorial */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <Video className="w-6 h-6 text-white" />
                <h2 className="text-2xl font-bold text-white">Watch Tutorial</h2>
              </div>
              <p className="text-blue-100 mt-2">Learn how to create and correctly label your KML file</p>
            </div>
            
            <div className="p-8">
              <p className="text-slate-600 mb-6 text-lg">
                Follow this comprehensive tutorial to understand the KML creation process on Google My Maps:
              </p>
              <div className="relative rounded-2xl overflow-hidden shadow-lg bg-slate-900">
                <video
                  className="w-full h-80 object-cover"
                  src="/static/images/KML_video.mp4"
                  title="KML Setup Tutorial"
                  controls
                  poster="/path/to/video-thumbnail.jpg"
                >
                  Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent pointer-events-none"></div>
              </div>
            </div>
          </div>

          {/* Step 2: Create KML */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <FileText className="w-6 h-6 text-white" />
                <h2 className="text-2xl font-bold text-white">Create Your KML</h2>
              </div>
              <p className="text-indigo-100 mt-2">Use Google My Maps to create your geospatial data file</p>
            </div>
            
            <div className="p-8">
              <div className="mb-8">
                <p className="text-slate-600 text-lg mb-4">
                  Access Google My Maps to create your KML file with proper labeling:
                </p>
                <a
                  href="https://www.google.com/intl/de_de/maps/about/mymaps/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span>Open Google My Maps</span>
                  <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </a>
              </div>

              {/* Naming Protocol Table */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  Naming Protocol Reference
                </h3>
                
                <div className="grid gap-4">
                  {namingProtocol.map((item, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                      <div className={`bg-gradient-to-r ${item.color} p-4`}>
                        <h4 className="text-white font-bold text-lg">{item.layer}</h4>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-slate-500 font-semibold uppercase tracking-wider mb-2">Example Name</p>
                            <p className="text-slate-800 font-mono bg-slate-100 px-3 py-2 rounded-lg">{item.example}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500 font-semibold uppercase tracking-wider mb-2">Selection Guide</p>
                            <p className="text-slate-700">{item.selection}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Upload */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <Upload className="w-6 h-6 text-white" />
                <h2 className="text-2xl font-bold text-white">Upload Your KML File</h2>
              </div>
              <p className="text-purple-100 mt-2">Select and upload your completed KML file</p>
            </div>
            
            <div className="p-8">
              <div className="mb-6">
                <FileUploadZone
                  onFilesSelect={handleUploadKML}
                  accept=".kml"
                  acceptedTypes={["KML"]}
                />
              </div>
              
              <UploadProgressDisplay progress={uploadProgress} />

              {uploadStatus && (
                <div className={`mt-6 p-4 border rounded-xl ${
                  uploadStatus === "success"
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}>
                  <div className="flex items-center gap-3">
                    {uploadStatus === "success" ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <p className={`font-semibold ${
                      uploadStatus === "success" ? "text-green-800" : "text-red-800"
                    }`}>
                      {uploadStatus === "success"
                        ? "KML file uploaded successfully! You can now proceed to the next step."
                        : "Error uploading KML file. Please check your file and try again."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Continue Button */}
          <div className="text-center">
            <button
              className={`group inline-flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg ${
                uploadStatus === "success"
                  ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white hover:shadow-xl"
                  : "bg-slate-200 text-slate-500 cursor-not-allowed"
              }`}
              onClick={handleContinue}
              disabled={uploadStatus !== "success"}
            >
              <span>Continue to Next Step</span>
              <ArrowRight className={`w-5 h-5 ${
                uploadStatus === "success" ? "group-hover:translate-x-1" : ""
              } transition-transform duration-200`} />
            </button>
            
            {uploadStatus !== "success" && (
              <p className="text-slate-500 mt-3 text-sm">
                Please upload your KML file to continue
              </p>
            )}
          </div>

        </div>
      </div>
    </MainLayout>
  );
};

export default KMLSetup;